import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import SiteLayout from "@/components/layout/SiteLayout";

import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesIndexPage from "@/pages/ServicesIndexPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import SubServiceDetailPage from "@/pages/SubServiceDetailPage";
import ProtectReadyPage from "@/pages/ProtectReadyPage";
import BlogPage from "@/pages/BlogPage";
import NewsPage from "@/pages/NewsPage";
import PostDetailPage from "@/pages/PostDetailPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import LegalPage from "@/pages/LegalPage";

const PRIVACY = `Dynamic Risks Ltd ("we", "us") is committed to protecting your privacy. This notice explains how we collect, use and share your personal information when you visit dynamicrisks.co.uk or engage our services.

We only collect data necessary to respond to your enquiry, deliver contracted services and comply with our legal obligations. Personal data submitted via our contact form is used solely to respond to your enquiry and is retained for as long as necessary for that purpose or as required by law.

We do not sell your data. We do not share your data with third parties for marketing purposes. Where third-party processors are used (for example, transactional email delivery), we ensure appropriate contractual and technical safeguards are in place.

You have the right to access, correct or request deletion of your personal data. Requests can be sent to contact@dynamicrisksgroup.com.`;

const COOKIES = `Dynamic Risks uses a small number of cookies to run the website effectively and, with your consent, to understand how it is used.

Essential cookies are strictly necessary for the site to function (for example, remembering your cookie choice). These are always set.

Analytics cookies are only set when you accept them. They help us understand which pages are most useful and how we can improve.

You can change your consent at any time by clearing your browser cookies for this site.`;

const TERMS = `These terms govern your use of dynamicrisks.co.uk. By using the site, you agree to these terms.

Content on this site is provided for general information only. It does not constitute professional advice and should not be relied upon in place of a formal engagement with Dynamic Risks Ltd.

The Dynamic Risks name, logo and materials are the property of Dynamic Risks Ltd. All rights reserved. No content may be reproduced without our prior written consent.

We reserve the right to update these terms from time to time. Any updates will be reflected on this page.`;

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function AdminRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin/*" element={<AdminDashboard />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollToTop />
                    <Routes>
                        {/* Admin routes without site layout */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/admin/*" element={<AdminDashboard />} />

                        {/* Public site with layout */}
                        <Route
                            path="/*"
                            element={
                                <SiteLayout>
                                    <Routes>
                                        <Route path="/" element={<HomePage />} />
                                        <Route path="/about" element={<AboutPage />} />
                                        <Route path="/services" element={<ServicesIndexPage />} />
                                        {/* Legacy TSCM route → new nested Security Consultancy location */}
                                        <Route path="/services/tscm" element={<Navigate to="/services/security-consultancy/tscm" replace />} />
                                        <Route path="/services/security-consultancy/martyns-law/protectready" element={<ProtectReadyPage />} />
                                        <Route path="/services/:slug/:subSlug" element={<SubServiceDetailPage />} />
                                        <Route path="/services/:slug" element={<ServiceDetailPage />} />
                                        <Route path="/blog" element={<BlogPage />} />
                                        <Route path="/blog/:slug" element={<PostDetailPage type="blog" />} />
                                        <Route path="/news" element={<NewsPage />} />
                                        <Route path="/news/:slug" element={<PostDetailPage type="news" />} />
                                        <Route path="/contact" element={<ContactPage />} />
                                        <Route path="/privacy" element={<LegalPage title="Privacy Policy" kicker="Legal" body={PRIVACY} />} />
                                        <Route path="/cookies" element={<LegalPage title="Cookie Notice" kicker="Legal" body={COOKIES} />} />
                                        <Route path="/terms" element={<LegalPage title="Terms of Use" kicker="Legal" body={TERMS} />} />
                                        <Route path="*" element={<HomePage />} />
                                    </Routes>
                                </SiteLayout>
                            }
                        />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
